"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.racquetVenue = exports.platformBounds = exports.NET_BOUNCE = exports.WALL_BOUNCE = void 0;
exports.racquetStage = racquetStage;
/**
 * The racquet courts: tennis, pickleball, padel and platform tennis, from
 * v1's CourtGeometry.
 *
 * The stage is the experiences protocol's, feature for feature. The arena is
 * what v1 approximates: a net of finite width whose top rises to the posts
 * and that stops balls from either side, and walls all the way round where
 * the sport has them, padel's taller back corners included.
 */
const models_1 = require("../../models");
const features_1 = require("./features");
const frame_1 = require("./frame");
const RACQUET_VENUE_IDS = {
    TENNIS: "tennis",
    PICKLEBALL: "pickleball",
    PADEL: "padel",
    PLATFORM_TENNIS: "platformTennis",
};
const { courtRestCoeff, netRestCoeff, wallRestCoeff } = models_1.DefaultPhysicalConstants;
exports.WALL_BOUNCE = { kind: "bounce", restitution: wallRestCoeff, tangential: 1 };
exports.NET_BOUNCE = { kind: "bounce", restitution: netRestCoeff, tangential: netRestCoeff };
/**
 * What every court has: the net, its posts and the boundary. The net's `z`
 * is the tape at the center; the posts stand at the lined court's sides.
 */
function courtBase(g) {
    const hw = g.COURT_WIDTH / 2;
    const hl = g.COURT_LENGTH / 2;
    return {
        center: (0, features_1.point)(0, 0),
        net: { ...(0, features_1.segment)(-hw, 0, hw, 0), z: { min: 0, max: g.NET_HEIGHT_CENTER } },
        net_post_left: { ...(0, features_1.point)(-hw, 0), z: { min: 0, max: g.NET_HEIGHT_POST } },
        net_post_right: { ...(0, features_1.point)(hw, 0), z: { min: 0, max: g.NET_HEIGHT_POST } },
        baseline_near: (0, features_1.segment)(-hw, -hl, hw, -hl),
        baseline_far: (0, features_1.segment)(-hw, hl, hw, hl),
        sideline_left: (0, features_1.segment)(-hw, -hl, -hw, hl),
        sideline_right: (0, features_1.segment)(hw, -hl, hw, hl),
    };
}
/** Service boxes between the net and the service line: tennis, padel, platform tennis. */
function serviceCourts(g) {
    const sw = g.SERVICE_WIDTH / 2;
    const sl = g.SERVICE_LENGTH / 2;
    return {
        service_line_near: (0, features_1.segment)(-sw, -sl, sw, -sl),
        service_line_far: (0, features_1.segment)(-sw, sl, sw, sl),
        center_service_line: (0, features_1.segment)(0, -sl, 0, sl),
        service_box_near_left: (0, features_1.box)(-sw, 0, -sl, 0),
        service_box_near_right: (0, features_1.box)(0, sw, -sl, 0),
        service_box_far_left: (0, features_1.box)(-sw, 0, 0, sl),
        service_box_far_right: (0, features_1.box)(0, sw, 0, sl),
    };
}
/** A centre mark's length, inward from the baseline: 4 in. */
const CENTER_MARK = (4 / 12) * 0.3048;
/**
 * Lines baseline to baseline inside a court narrower inside than out (tennis
 * singles sidelines, platform tennis alley lines), named by the sport, and
 * the centre mark bisecting each baseline.
 */
function innerLines(g, name) {
    const sw = g.SERVICE_WIDTH / 2;
    const hl = g.COURT_LENGTH / 2;
    return {
        [`${name}_left`]: (0, features_1.segment)(-sw, -hl, -sw, hl),
        [`${name}_right`]: (0, features_1.segment)(sw, -hl, sw, hl),
        center_mark_near: (0, features_1.segment)(0, -hl, 0, -hl + CENTER_MARK),
        center_mark_far: (0, features_1.segment)(0, hl, 0, hl - CENTER_MARK),
    };
}
/** The inner lines' name where a sport has them. */
const INNER_LINES = {
    TENNIS: "singles_sideline",
    PLATFORM_TENNIS: "alley_line",
};
/** Pickleball: the kitchen against the net, service boxes from it to the baseline. */
function kitchenCourts(g) {
    const hl = g.COURT_LENGTH / 2;
    const sw = g.SERVICE_WIDTH / 2;
    const sl = g.SERVICE_LENGTH / 2;
    return {
        kitchen_line_near: (0, features_1.segment)(-sw, -sl, sw, -sl),
        kitchen_line_far: (0, features_1.segment)(-sw, sl, sw, sl),
        center_line_near: (0, features_1.segment)(0, -hl, 0, -sl),
        center_line_far: (0, features_1.segment)(0, sl, 0, hl),
        service_box_near_left: (0, features_1.box)(-sw, 0, -hl, -sl),
        service_box_near_right: (0, features_1.box)(0, sw, -hl, -sl),
        service_box_far_left: (0, features_1.box)(-sw, 0, sl, hl),
        service_box_far_right: (0, features_1.box)(0, sw, sl, hl),
    };
}
/** The protocol's stage for a racquet sport (experiences `stageFor`). */
function racquetStage(sport) {
    const g = models_1.CourtGeometryBySport[sport];
    return {
        extents: { x: [-g.COURT_WIDTH / 2, g.COURT_WIDTH / 2], y: [-g.COURT_LENGTH / 2, g.COURT_LENGTH / 2] },
        features: {
            ...courtBase(g),
            ...(sport === "PICKLEBALL" ? kitchenCourts(g) : serviceCourts(g)),
            ...(INNER_LINES[sport] ? innerLines(g, INNER_LINES[sport]) : {}),
        },
    };
}
/** The platform's half extents: the ground the ball can be on. */
const platformBounds = (g) => ({
    x: [-g.PLATFORM_WIDTH / 2, g.PLATFORM_WIDTH / 2],
    y: [-g.PLATFORM_LENGTH / 2, g.PLATFORM_LENGTH / 2],
});
exports.platformBounds = platformBounds;
/**
 * Walls round the platform's edge, counterclockwise from the far left
 * corner: left, right, far, near. The side walls are WALL_HEIGHT tall, the
 * end walls TALL_WALL_HEIGHT where the sport has one. A padel side wall is
 * three panels: its TALL_WALL_SIDE_LENGTH at each end, as tall as the end
 * walls, are the corners `leftWallFarCorner`, `leftWallNearCorner`, and so
 * on, and `leftWall` / `rightWall` are the lower stretch between them.
 */
function walls(g) {
    if (g.WALL_HEIGHT === 0) {
        return [];
    }
    const side = g.WALL_HEIGHT;
    const end = g.TALL_WALL_HEIGHT !== 0 ? g.TALL_WALL_HEIGHT : g.WALL_HEIGHT;
    const hw = g.PLATFORM_WIDTH / 2;
    const { nearLeft, nearRight, farRight, farLeft } = (0, features_1.corners)(hw, g.PLATFORM_LENGTH / 2);
    const ends = [(0, features_1.wall)("farWall", farRight, farLeft, end, exports.WALL_BOUNCE), (0, features_1.wall)("nearWall", nearLeft, nearRight, end, exports.WALL_BOUNCE)];
    if (g.TALL_WALL_SIDE_LENGTH === 0) {
        return [
            (0, features_1.wall)("leftWall", farLeft, nearLeft, side, exports.WALL_BOUNCE),
            (0, features_1.wall)("rightWall", nearRight, farRight, side, exports.WALL_BOUNCE),
            ...ends,
        ];
    }
    const inner = g.PLATFORM_LENGTH / 2 - g.TALL_WALL_SIDE_LENGTH;
    const at = (x, y) => ({ x, y });
    return [
        (0, features_1.wall)("leftWallFarCorner", farLeft, at(-hw, inner), end, exports.WALL_BOUNCE),
        (0, features_1.wall)("leftWall", at(-hw, inner), at(-hw, -inner), side, exports.WALL_BOUNCE),
        (0, features_1.wall)("leftWallNearCorner", at(-hw, -inner), nearLeft, end, exports.WALL_BOUNCE),
        (0, features_1.wall)("rightWallNearCorner", nearRight, at(hw, -inner), end, exports.WALL_BOUNCE),
        (0, features_1.wall)("rightWall", at(hw, -inner), at(hw, inner), side, exports.WALL_BOUNCE),
        (0, features_1.wall)("rightWallFarCorner", at(hw, inner), farRight, end, exports.WALL_BOUNCE),
        ...ends,
    ];
}
/** The net post to post, sagging from NET_HEIGHT_POST to NET_HEIGHT_CENTER, stopping balls from both sides. */
const net = (g) => ({
    id: "net",
    kind: "net",
    from: { x: -g.NET_WIDTH / 2, y: 0 },
    to: { x: g.NET_WIDTH / 2, y: 0 },
    bottom: 0,
    top: g.NET_HEIGHT_CENTER,
    topAtEnds: g.NET_HEIGHT_POST,
    faces: "both",
    response: exports.NET_BOUNCE,
});
function racquetArena(sport) {
    const g = models_1.CourtGeometryBySport[sport];
    return {
        bounds: (0, exports.platformBounds)(g),
        ground: { restitution: courtRestCoeff },
        panels: [...walls(g), net(g)],
    };
}
/** The regulation court a racquet sport is played on. */
const racquetVenue = (sport) => ({
    id: RACQUET_VENUE_IDS[sport],
    sport,
    stage: racquetStage(sport),
    arena: racquetArena(sport),
    frame: (0, frame_1.v1Frame)(sport),
});
exports.racquetVenue = racquetVenue;
